import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthGuard } from "./authGuard";
import { AppLayout } from "@/layout/AppLayout";
import { InitializePage } from "@/features/auth/pages/InitializePage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { TaskListContent } from "@/features/tasks/pages/TaskListContent";
import { AllTasksContent } from "@/features/tasks/pages/AllTasksContent";
import { NewTasksContent } from "@/features/tasks/pages/NewTasksContent";
import { TaskDetailsContent } from "@/features/tasks/pages/TaskDetailsContent";
import { CannotCompleteTaskPage } from "@/features/tasks/pages/CannotCompleteTaskPage";
import { ConfirmCompleteTaskPage } from "@/features/tasks/pages/ConfirmCompleteTaskPage";
import { RescheduleTaskPage } from "@/features/tasks/pages/RescheduleTaskPage";
import { MessagesContent } from "@/features/messages/pages/MessagesContent";
import { LeaveHistoryPage } from "@/features/leave/pages/LeaveHistoryPage";
import { ShiftsPage } from "@/features/shifts/pages/ShiftsPage";
import { ScheduledShiftsPage } from "@/features/shifts/pages/ScheduledShiftsPage";
import { ConfirmStartShiftPage } from "@/features/shifts/pages/ConfirmStartShiftPage";
import { ConfirmEndShiftPage } from "@/features/shifts/pages/ConfirmEndShiftPage";
import { ShiftDetailsPage } from "@/features/shifts/pages/ShiftDetailsPage";
import { ScanSuccessPage } from "@/features/patrol/pages/ScanSuccessPage";
import { ScanFailedPage } from "@/features/patrol/pages/ScanFailedPage";
import { useAuthContext } from "@/lib/authContext";
import { deferredDeepLinkService } from "@/lib/deferredDeepLink";

export function AppRouter() {
  const { isAuthenticated, isLoading, isInitialized } = useAuthContext();
  const location = useLocation();
  const pendingUid = localStorage.getItem("pendingInviteUid");
  const deferredUid = localStorage.getItem("deferredInviteUid");
  const deferredData = deferredDeepLinkService.getDeferredData();
  
  // Use either pending, deferred localStorage, or deferred service data
  const inviteUid = pendingUid || deferredUid || deferredData?.uid;

  if (!isInitialized || isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse">Loading...</div></div>;
  }

  // Safety check for location
  if (!location) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse">Loading...</div></div>;
  }

  const path = location?.pathname ?? '/';
  const wantsInitialize = !isAuthenticated && inviteUid && path !== "/initialize";

  return (
    <Routes>
        {/* Public routes */}
        <Route path="/initialize" element={<InitializePage />} />
        <Route 
          path="/login" 
          element={isAuthenticated ? <Navigate to="/shifts" replace /> : <LoginPage />} 
        />

        {/* Protected routes (layout owns IonPage/Header/Content) */}
        {/* Task routes redirect to shifts */}
        <Route
          path="/tasks"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/tasks/all"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/unviewed-tasks"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/tasks/:taskId/cannot-complete"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/tasks/:taskId/confirm-complete"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/tasks/:taskId/reschedule"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/tasks/:taskId"
          element={
            <AuthGuard>
              <Navigate to="/shifts" replace />
            </AuthGuard>
          }
        />
        <Route
          path="/messages"
          element={
            <AuthGuard>
              <AppLayout>
                <MessagesContent />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/leave"
          element={
            <AuthGuard>
              <AppLayout>
                <LeaveHistoryPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/shifts"
          element={
            <AuthGuard>
              <AppLayout>
                <ScheduledShiftsPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/shifts/history"
          element={
            <AuthGuard>
              <AppLayout>
                <ShiftsPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/shifts/confirm-start"
          element={
            <AuthGuard>
              <AppLayout>
                <ConfirmStartShiftPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/shifts/confirm-end"
          element={
            <AuthGuard>
              <AppLayout>
                <ConfirmEndShiftPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/shifts/:shiftId"
          element={
            <AuthGuard>
              <AppLayout>
                <ShiftDetailsPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route path="/patrol/scan" element={<Navigate to="/shifts" replace />} />
        <Route
          path="/patrol/scan/success"
          element={
            <AuthGuard>
              <AppLayout>
                <ScanSuccessPage />
              </AppLayout>
            </AuthGuard>
          }
        />
        <Route
          path="/patrol/scan/failed"
          element={
            <AuthGuard>
              <AppLayout>
                <ScanFailedPage />
              </AppLayout>
            </AuthGuard>
          }
        />

        {/* Default redirect */}
        <Route 
          path="/" 
          element={
            <Navigate 
              to={
                wantsInitialize
                  ? `/initialize?uid=${encodeURIComponent(inviteUid!)}`
                  : isAuthenticated
                  ? "/shifts"
                  : "/login"
              } 
              replace 
            />
          } 
        />

        {/* If user lands on anything else while we have a pending uid, send them to initialize */}
        {wantsInitialize && (
          <Route 
            path="*" 
            element={<Navigate to={`/initialize?uid=${encodeURIComponent(inviteUid!)}`} replace />} 
          />
        )}
      </Routes>
  );
}